import {
  resetButtonStyle,
  ArrowLeftIcon,
  ArrowRightIcon
} from '@kvdbil/components';
import React, { useState } from 'react';
import styled from 'styled-components';
import { useScroller } from '../hooks/useScroller';
import VehicleCardListContainer from '~/App/shared/components/VehicleCardList/components/VehicleCardListContainer';
import VehicleCardListItem from '~/App/shared/components/VehicleCardList/components/VehicleCardListItem';
import { PartialMQSizeValues } from '~/helpers/mq';
import { useIsDevice } from '../hooks/useIsDevice';
import { InView } from 'react-intersection-observer';
import { useTranslation } from '~/Locale';

const Container = styled.div`
  position: relative;
  width: 100%;
`;

type ButtonProps = {
  $shouldDisplay: boolean;
  $buttonColor?: string;
};

const Button = styled.button<ButtonProps>`
  ${resetButtonStyle};
  background: ${({ theme, $buttonColor }) =>
    $buttonColor ? $buttonColor : theme.colors.secondary.mainAlt};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s ease-out;

  position: absolute;
  z-index: 8;
  top: calc(50% - 1.125rem);
  display: ${({ $shouldDisplay }) => ($shouldDisplay ? 'flex' : 'none')};

  color: ${({ theme }) => theme.colors.common.white};
  font-size: 0.75rem;
  position: absolute;
  height: 2.25em;
  width: 2.25em;
  border-radius: 50%;
  svg {
    font-size: 0.9375rem;
  }
`;

const ButtonLeft = styled(Button)`
  left: -0.375rem;
`;
const ButtonRight = styled(Button)`
  right: -0.375rem;
`;

const SwipeIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 1rem 1.6rem 0.5rem;
  gap: 2px;
`;

const Indication = styled.div<{ active: boolean }>`
  background-color: ${({ theme, active }) =>
    active ? theme.colors.primary.main : theme.colors.grayscaleToned.light5};
  border-radius: 0.5rem;
  height: 0.25rem;
  width: 100%;
  transition: background-color 0.3s;
`;

export interface SlideShowProps {
  children: React.ReactNode[];
  alwaysShowPagination?: boolean;
  itemsPerSlide?: PartialMQSizeValues;
  showIndicator?: boolean;
  buttonColor?: string;
}

export const SlideShow = ({
  children,
  alwaysShowPagination = false,
  itemsPerSlide,
  buttonColor
}: SlideShowProps) => {
  const { t } = useTranslation();
  const [activeItem, setActiveItem] = useState(0);
  const {
    handleMoveClick,
    scroller,
    childRefs,
    onScroll,
    leftDisabled,
    rightDisabled
  } = useScroller(children);

  const { isMobile } = useIsDevice();

  return (
    <Container>
      <ButtonLeft
        onClick={() => handleMoveClick('left')}
        data-testid="left-arrow"
        aria-label={t('Previous auction card')}
        $shouldDisplay={!leftDisabled && (alwaysShowPagination || !isMobile)}
        $buttonColor={buttonColor}
      >
        <ArrowLeftIcon />
      </ButtonLeft>

      <VehicleCardListContainer
        innerRef={scroller}
        onScroll={onScroll}
        isSlider
      >
        {children?.map((item, index) => {
          return (
            <VehicleCardListItem
              key={index}
              innerRef={childRefs[index]}
              {...itemsPerSlide}
            >
              <InView
                threshold={[0.5, 1]}
                onChange={inView => inView && setActiveItem(index)}
              >
                {({ ref }) => <div ref={ref}>{item}</div>}
              </InView>
            </VehicleCardListItem>
          );
        })}
      </VehicleCardListContainer>

      <ButtonRight
        onClick={() => handleMoveClick('right')}
        data-testid="right-arrow"
        aria-label={t('Next auction card')}
        $shouldDisplay={!rightDisabled && (alwaysShowPagination || !isMobile)}
        $buttonColor={buttonColor}
      >
        <ArrowRightIcon />
      </ButtonRight>

      <SwipeIndicator>
        {isMobile &&
          children?.map((_, index) => (
            <Indication key={index} active={index === activeItem} />
          ))}
      </SwipeIndicator>
    </Container>
  );
};

export default SlideShow;
